import type { ReactNode } from "react";

import {
  NullGuard,
  PercentageChangeField,
  PriceField,
  ValueField,
  VolumePercentageField,
} from "~/components/tableField";
import {
  EndDateCell,
  MarketCell,
  StatusCell,
  TradeButton,
} from "~/feature/event/components/MarketsDataGrid/renderCells";
import { SpreadCell } from "~/feature/event/components/MarketsDataGrid/renderCells/SpreadCell";
import {
  AverageCell,
  ChanceGroupedCell,
  NoteCell,
  PriceGroupedCell,
  SpreadGroupedCell,
} from "~/feature/event/components/MarketsDataGrid/renderGroupedCells";
import {
  type IMarketsDataGridMarketRow,
  type IMarketsDataGridRow,
  isMarketsDataGridGroupRow,
  isMarketsDataGridMarketRow,
  isMarketsDataGridNoteRow,
  MarketsDataGridField,
} from "~/feature/event/components/MarketsDataGrid/types";
import type { IAggregatedEvent } from "~/types/event";
import { isPriceUnavailable } from "~/utils/marketValue";

type MarketsDataGridCell = (
  row: IMarketsDataGridRow,
  event: IAggregatedEvent
) => ReactNode;

const marketOnly =
  (
    render: (
      market: IMarketsDataGridMarketRow,
      event: IAggregatedEvent
    ) => ReactNode
  ): MarketsDataGridCell =>
  (row, event) =>
    isMarketsDataGridMarketRow(row) ? render(row, event) : null;

export const marketsDataGridCellByField: Record<
  MarketsDataGridField,
  MarketsDataGridCell
> = {
  [MarketsDataGridField.Market]: (row) => {
    if (isMarketsDataGridMarketRow(row)) {
      return (
        <MarketCell
          isTrending={row.isTrending}
          isBreaking={row.isBreaking}
          title={row.title || ""}
          platform={row.platform}
        />
      );
    }

    if (isMarketsDataGridNoteRow(row)) {
      return <NoteCell text={row.text} />;
    }

    return isMarketsDataGridGroupRow(row) ? (
      <AverageCell outcomeTitle={row.outcomeTitle} />
    ) : null;
  },
  [MarketsDataGridField.Trade]: marketOnly((market, event) => (
    <TradeButton market={market} event={event} />
  )),
  [MarketsDataGridField.Chance]: (row) => {
    if (isMarketsDataGridMarketRow(row)) {
      return <PercentageChangeField market={row} />;
    }

    return isMarketsDataGridGroupRow(row) ? (
      <ChanceGroupedCell outcome={row.outcome} />
    ) : null;
  },
  [MarketsDataGridField.Price]: (row) => {
    if (isMarketsDataGridMarketRow(row)) {
      return (
        <PriceField
          price={row.price || 0}
          priceNo={row.priceNo}
          withText={true}
          NA={isPriceUnavailable(row.volume, row.price)}
        />
      );
    }

    return isMarketsDataGridGroupRow(row) ? (
      <PriceGroupedCell outcome={row.outcome} />
    ) : null;
  },
  [MarketsDataGridField.Spread]: (row) => {
    if (isMarketsDataGridMarketRow(row)) {
      return (
        <SpreadCell
          spread={row.spread ?? null}
          bestAsk={row.bestAsk ?? null}
          bestBid={row.bestBid ?? null}
          lastTradePrice={row.lastTradePrice ?? null}
        />
      );
    }

    return isMarketsDataGridGroupRow(row) ? (
      <SpreadGroupedCell outcome={row.outcome} />
    ) : null;
  },
  [MarketsDataGridField.Liquidity]: marketOnly((market) => (
    <NullGuard value={market.liquidity}>
      <ValueField value={market.liquidity} />
    </NullGuard>
  )),
  [MarketsDataGridField.Volume]: marketOnly((market) => (
    <ValueField value={market.volume} />
  )),
  [MarketsDataGridField.H24]: marketOnly((market) => (
    <VolumePercentageField
      percentage={market.volume24pers}
      createdAt={market.createdAt}
      marketStatus={market.unifiedStatus}
      periodDays={1}
    />
  )),
  [MarketsDataGridField.D7]: marketOnly((market) => (
    <VolumePercentageField
      percentage={market.volume7pers}
      createdAt={market.createdAt}
      marketStatus={market.unifiedStatus}
      periodDays={7}
    />
  )),
  [MarketsDataGridField.OpenInterest]: marketOnly((market) => (
    <NullGuard value={market.openInterest}>
      <ValueField value={market.openInterest} />
    </NullGuard>
  )),
  [MarketsDataGridField.EndsIn]: marketOnly((market) => (
    <EndDateCell endDate={market.endDate} marketStatus={market.unifiedStatus} />
  )),
  [MarketsDataGridField.Status]: marketOnly((market) => (
    <StatusCell market={market} />
  )),
};

export const MARKETS_DATA_GRID_FIELDS = Object.values(MarketsDataGridField);
